import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import { Box, IconButton, List, ListItem, ListItemText, TextField, Typography } from "@mui/material";
import { type FC, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { NEUTRAL } from "../../theme/tokens";
import type { LocationItem } from "../../utils/models";

type LocationSectionProps = { title: string; items: LocationItem[]; setItems: (items: LocationItem[]) => void };

export const LocationSection: FC<LocationSectionProps> = ({ title, items, setItems }) => {
	const { t } = useTranslation();
	const [newValue, setNewValue] = useState<LocationItem["value"]>("");
	const [showInput, setShowInput] = useState<boolean>(false);
	const [editingItem, setEditingItem] = useState<LocationItem>();
	const [itemToDelete, setItemToDelete] = useState<string>();

	const handleAdd = (e?: MouseEvent) => {
		e?.stopPropagation();

		if (newValue.trim()) {
			const newItem: LocationItem = {
				id: `${title.toLowerCase()}-${newValue.trim()}-${Date.now()}-${Math.random()}`,
				value: newValue.trim(),
			};
			setItems([...items, newItem]);
			setNewValue("");
			setShowInput(false);
		}
	};

	const handleDeleteClick = (e: MouseEvent, id: string) => {
		e.stopPropagation();
		setItemToDelete(id);
	};

	const handleDeleteConfirm = () => {
		if (itemToDelete) setItems(items.filter((item) => item.id !== itemToDelete));
		setItemToDelete(undefined);
	};

	const itemToDeleteName = items.find((item) => item.id === itemToDelete)?.value;

	const handleStartEdit = (e: MouseEvent, item: LocationItem) => {
		e.stopPropagation();
		setEditingItem(item);
	};

	const handleSaveEdit = (newValue: string) => {
		if (editingItem && newValue.trim()) {
			setItems(items.map((item) => (item.id === editingItem.id ? { ...item, value: newValue.trim() } : item)));
			setEditingItem(undefined);
		}
	};

	return (
		<>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "primary.main" }}>
					{title}
				</Typography>
				<SystemButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						setShowInput(true);
					}}
					leadingIcon={<Add />}
					tooltip={title === t("units") ? t("add.unit") : t("add.floor")}
					sx={{ minWidth: "auto", px: 1, bgcolor: "transparent", color: "primary.main", "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }}
				/>
			</Box>
			{showInput && (
				<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
					<TextField
						size="small"
						value={newValue}
						onChange={(e) => setNewValue(e.target.value)}
						placeholder={title === t("units") ? t("enter.unit.name") : t("enter.floor.name")}
						onKeyDown={(e) => {
							e.stopPropagation();
							if (e.key === "Enter") handleAdd();
						}}
						autoFocus
						fullWidth
					/>
					<SystemButton size="small" onClick={(e) => handleAdd(e)} tooltip={title === t("units") ? t("add.unit") : t("add.floor")}>
						{t("add")}
					</SystemButton>
				</Box>
			)}
			<List dense>
				{items.map((item) => (
					<ListItem
						key={item.id}
						sx={{
							border: `1px solid ${NEUTRAL[300]}`,
							borderRadius: 1,
							mb: 0.5,
							"&:hover": { bgcolor: NEUTRAL[100] },
						}}
						secondaryAction={
							<IconButton edge="end" size="small" onClick={(e) => handleDeleteClick(e, item.id)}>
								<Delete fontSize="small" />
							</IconButton>
						}
					>
						{editingItem?.id === item.id ? (
							<TextField
								size="small"
								defaultValue={editingItem.value}
								onBlur={(e) => handleSaveEdit(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										handleSaveEdit((e.target as HTMLInputElement).value);
									}
								}}
								autoFocus
								fullWidth
							/>
						) : (
							<ListItemText primary={item.value} onClick={(e) => handleStartEdit(e, item)} sx={{ cursor: "pointer" }} />
						)}
					</ListItem>
				))}
			</List>
			<SystemDialog
				open={!!itemToDelete}
				onClose={() => setItemToDelete(undefined)}
				title={t("delete.location")}
				body={t("delete.location.confirm", { name: itemToDeleteName })}
				confirm={t("delete")}
				cancel={t("cancel")}
				onConfirm={handleDeleteConfirm}
			/>
		</>
	);
};
